/**
 * Request Optimization Utilities
 * 
 * Reduces API calls and improves dashboard performance by:
 * 1. Batching similar requests
 * 2. Implementing request deduplication
 * 3. Adding intelligent caching
 */

import { message } from 'antd';

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

class RequestOptimizer {
  private pendingRequests = new Map<string, PendingRequest>();
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  
  // Request deduplication - prevent duplicate requests
  async deduplicateRequest<T>(
    key: string, 
    requestFn: () => Promise<T>,
    ttl: number = 30000 // 30 seconds default TTL
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }

    // Check if request is already pending
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending.promise;
    }

    // Create new request
    const promise = new Promise<T>((resolve, reject) => {
      const pendingRequest: PendingRequest = {
        promise: requestFn().then(result => {
          // Cache successful result
          this.cache.set(key, {
            data: result,
            timestamp: Date.now(),
            ttl
          });
          
          // Clean up pending request
          this.pendingRequests.delete(key);
          
          resolve(result);
          return result;
        }).catch(error => {
          // Clean up pending request on error
          this.pendingRequests.delete(key);
          reject(error);
          throw error;
        }),
        timestamp: Date.now(),
        resolve,
        reject
      };
      
      this.pendingRequests.set(key, pendingRequest);
    });

    return promise;
  }

  // Clear cache (useful for force refresh)
  clearCache(pattern?: string) {
    if (pattern) {
      for (const key of Array.from(this.cache.keys())) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // Get cache stats for debugging
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cacheKeys: Array.from(this.cache.keys()),
      pendingKeys: Array.from(this.pendingRequests.keys())
    };
  }
}

// Global instance
export const requestOptimizer = new RequestOptimizer();

// Hook for React components
export const useRequestOptimizer = () => {
  return {
    deduplicateRequest: requestOptimizer.deduplicateRequest.bind(requestOptimizer),
    clearCache: requestOptimizer.clearCache.bind(requestOptimizer),
    getStats: requestOptimizer.getCacheStats.bind(requestOptimizer)
  };
};

// Utility to create cache keys
export const createCacheKey = (endpoint: string, params: Record<string, any> = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  
  return sortedParams ? `${endpoint}?${sortedParams}` : endpoint;
};

// Request timeout utility
export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 10000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs)
    )
  ]);
};

// Batch similar requests
export class RequestBatcher {
  private batches = new Map<string, Array<{ resolve: Function; reject: Function }>>();
  private batchTimeout = 100; // 100ms batch window

  async batchRequest<T>(
    batchKey: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const batch = this.batches.get(batchKey) || [];
      batch.push({ resolve, reject });
      this.batches.set(batchKey, batch);

      // Execute batch after timeout
      if (batch.length === 1) {
        setTimeout(() => this.executeBatch(batchKey, requestFn), this.batchTimeout);
      }
    });
  }

  private async executeBatch<T>(batchKey: string, requestFn: () => Promise<T>) {
    const batch = this.batches.get(batchKey) || [];
    this.batches.delete(batchKey);

    try {
      const result = await requestFn();
      batch.forEach(({ resolve }) => resolve(result));
    } catch (error) {
      batch.forEach(({ reject }) => reject(error));
    }
  }
}

export const requestBatcher = new RequestBatcher();
